/**
 * employeeStore — 管理员端与用户端员工数据同步 Store
 * Admin 写入，用户端人才库读取，无需后端即可同步
 * 使用 localStorage 持久化，刷新页面后数据不丢失
 *
 * 头像数据（base64 data URL）单独存储在 IndexedDB（avatarDB），
 * 避免 localStorage 5MB 限制导致的静默丢失问题。
 */

import { setAvatar, getAllAvatars, deleteAvatar, isBase64DataUrl, IDB_AVATAR_MARKER } from './avatarDB';

/**
 * 管理端 jobCategory 简称 → 用户端完整分类名映射
 * 管理端用简称（如"客服"），用户端用完整名（如"客服售后类"）
 */
const ADMIN_CATEGORY_TO_USER_CATEGORY: Record<string, string> = {
  '客服':     '客服售后类',
  '售后':     '客服售后类',
  '客服售后': '客服售后类',
  '销售':     '销售市场类',
  '市场':     '销售市场类',
  '销售市场': '销售市场类',
  '运营':     '数据运营类',
  '数据':     '数据运营类',
  '数据运营': '数据运营类',
  '技术':     '技术开发类',
  '开发':     '技术开发类',
  '技术开发': '技术开发类',
  '设计':     '创意设计类',
  '创意':     '创意设计类',
  '创意设计': '创意设计类',
  '法务':     '法务合规类',
  '合规':     '法务合规类',
  '法务合规': '法务合规类',
  '人事':     '人力资源类',
  '人力':     '人力资源类',
  '人力资源': '人力资源类',
  '产品':     '产品设计类',
  '产品设计': '产品设计类',
  '媒体':     '媒体运营类',
  '新媒体':   '媒体运营类',
  '媒体运营': '媒体运营类',
  '项目':     '项目管理运营类',
  '项目管理': '项目管理运营类',
  '生产':     '生产管理岗',
  '生产管理': '生产管理岗',
};

/** 将管理端分类名规范化为用户端分类名 */
function normalizeCategory(cat: string | undefined): string | undefined {
  if (!cat) return undefined;
  // 已经是完整分类名，直接返回
  if (cat.endsWith('类') || cat.endsWith('岗')) return cat;
  // 尝试映射
  return ADMIN_CATEGORY_TO_USER_CATEGORY[cat] ?? cat;
}

export interface EmployeeUserOverride {
  name?: string;
  avatar?: string;
  description?: string;
  tags?: string[];
  salary?: string;
  role?: string;
  category?: string;
  visible?: boolean;
  status?: string;
  visibleInUserSide?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  teamConfig?: Record<string, any>;
}

export interface NewUserEmployee {
  id: string;
  name: string;
  role: string;
  avatar: string;
  description: string;
  tags: string[];
  salary: string;
  category: string;
  reviews: { rating: number }[];
  cases: { tags: string[] }[];
}

interface StoreState {
  version: number;
  overrides: Map<string, EmployeeUserOverride>;
  newEmployees: NewUserEmployee[];
  syncedIds: Set<string>;
  syncTimestamps: Map<string, number>;
}

const STORAGE_KEY = 'yh_employee_store_v2';

// ── 从 localStorage 恢复状态 ──────────────────────────────
function loadFromStorage(): Partial<StoreState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const overrides = new Map<string, EmployeeUserOverride>(
      Array.isArray(parsed.overrides) ? parsed.overrides : []
    );
    const newEmployees: NewUserEmployee[] = Array.isArray(parsed.newEmployees) ? parsed.newEmployees : [];
    const syncedIds = new Set<string>(Array.isArray(parsed.syncedIds) ? parsed.syncedIds : []);
    const syncTimestamps = new Map<string, number>(
      Array.isArray(parsed.syncTimestamps) ? parsed.syncTimestamps : []
    );
    return { overrides, newEmployees, syncedIds, syncTimestamps };
  } catch {
    return {};
  }
}

// ── 将当前状态写入 localStorage（base64 avatar 不写入，存 IDB 标记）──
function saveToStorage(s: StoreState): void {
  try {
    // 序列化 overrides 时，把 base64 头像替换为 IDB 占位符
    const safeOverrides = Array.from(s.overrides.entries()).map(([id, ov]) => {
      if (isBase64DataUrl(ov.avatar)) {
        return [id, { ...ov, avatar: IDB_AVATAR_MARKER }] as [string, EmployeeUserOverride];
      }
      return [id, ov] as [string, EmployeeUserOverride];
    });
    // 对 newEmployees 中的 base64 头像也做相同处理
    const safeNewEmps = s.newEmployees.map(e =>
      isBase64DataUrl(e.avatar) ? { ...e, avatar: IDB_AVATAR_MARKER } : e
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      overrides:      safeOverrides,
      newEmployees:   safeNewEmps,
      syncedIds:      Array.from(s.syncedIds),
      syncTimestamps: Array.from(s.syncTimestamps.entries()),
    }));
  } catch {
    // quota exceeded — ignore (base64 already stripped, so this should rarely happen)
  }
}

const loaded = loadFromStorage();
const state: StoreState = {
  version:        0,
  overrides:      loaded.overrides       ?? new Map(),
  newEmployees:   loaded.newEmployees    ?? [],
  syncedIds:      loaded.syncedIds       ?? new Set(),
  syncTimestamps: loaded.syncTimestamps  ?? new Map(),
};

// ── 内存头像缓存（存放从 IndexedDB 加载或当次会话写入的 base64 头像）──
const _avatarCache = new Map<string, string>();

// ── 监听器集合 ──────────────────────────────────────────────
const listeners: Set<() => void> = new Set();

const notify = () => {
  state.version++;
  listeners.forEach(l => l());
};

// ── 模块加载时立即预热 IDB 头像缓存 ──────────────────────────
// 目的：让 IDB 读取尽早开始，避免用户侧人才库首次渲染时因缓存未就绪而显示旧头像
function _preloadAvatarCacheOnModuleLoad(): void {
  // 检查是否有 override 使用了 IDB_AVATAR_MARKER，有才需要预热
  const needsIdb = Array.from(state.overrides.values()).some(
    ov => ov.avatar === IDB_AVATAR_MARKER
  );
  if (!needsIdb) return;
  getAllAvatars()
    .then(idbAvatars => {
      let updated = false;
      idbAvatars.forEach((dataUrl, id) => {
        if (!_avatarCache.has(id) || _avatarCache.get(id) !== dataUrl) {
          _avatarCache.set(id, dataUrl);
          updated = true;
        }
      });
      if (updated) notify();
    })
    .catch(() => { /* IDB 预热失败时静默处理，loadAvatarCache 会在组件挂载时补充 */ });
}
_preloadAvatarCacheOnModuleLoad();

export const employeeStore = {
  getVersion: () => state.version,

  /**
   * 设置员工覆写数据。
   * 如果 override.avatar 是 base64 data URL，自动存入 IndexedDB + 内存缓存，
   * 避免 localStorage 5MB 限制。
   */
  setOverride: (id: string, override: EmployeeUserOverride) => {
    const current = state.overrides.get(id) || {};
    const merged = { ...current, ...override };

    // 规范化 category：将管理端简称映射为用户端完整分类名
    if (override.category !== undefined) {
      merged.category = normalizeCategory(override.category);
    }

    // 如果 avatar 是 base64，异步存 IndexedDB + 内存缓存，localStorage 存占位符
    if (isBase64DataUrl(override.avatar)) {
      _avatarCache.set(id, override.avatar!);
      setAvatar(id, override.avatar!); // fire-and-forget, errors logged in avatarDB
      merged.avatar = IDB_AVATAR_MARKER;
    } else if (override.avatar !== undefined && override.avatar !== IDB_AVATAR_MARKER) {
      // 普通 URL 头像：清除 IDB 中可能存在的旧 base64
      _avatarCache.delete(id);
      deleteAvatar(id);
      merged.avatar = override.avatar;
    } else if (override.avatar === undefined) {
      // 不改变头像，保留原来的
      merged.avatar = current.avatar;
    }

    state.overrides.set(id, merged);
    state.syncedIds.add(id);
    state.syncTimestamps.set(id, Date.now());
    saveToStorage(state);
    notify();
  },

  getOverride: (id: string) => state.overrides.get(id),

  deleteEmployee: (id: string) => {
    const current = state.overrides.get(id) || {};
    state.overrides.set(id, { ...current, visible: false });
    state.syncedIds.add(id);
    state.syncTimestamps.set(id, Date.now());
    _avatarCache.delete(id);
    deleteAvatar(id);
    saveToStorage(state);
    notify();
  },

  addEmployee: (emp: NewUserEmployee) => {
    const processedEmp = { ...emp };
    if (isBase64DataUrl(emp.avatar)) {
      _avatarCache.set(emp.id, emp.avatar);
      setAvatar(emp.id, emp.avatar);
      processedEmp.avatar = IDB_AVATAR_MARKER;
    }
    const existing = state.newEmployees.findIndex(e => e.id === emp.id);
    if (existing >= 0) {
      state.newEmployees[existing] = processedEmp;
    } else {
      state.newEmployees.push(processedEmp);
    }
    state.syncedIds.add(emp.id);
    state.syncTimestamps.set(emp.id, Date.now());
    saveToStorage(state);
    notify();
  },

  isSynced:     (id: string) => state.syncedIds.has(id),
  getSyncTime:  (id: string): number | undefined => state.syncTimestamps.get(id),
  getSyncedIds: () => new Set(state.syncedIds),

  /**
   * 从 IndexedDB 加载头像缓存（异步，应在组件挂载时调用）。
   * 加载完成后自动 notify，触发使用头像数据的组件重渲染。
   */
  loadAvatarCache: async (): Promise<void> => {
    try {
      const idbAvatars = await getAllAvatars();
      let updated = false;
      idbAvatars.forEach((dataUrl, id) => {
        if (!_avatarCache.has(id) || _avatarCache.get(id) !== dataUrl) {
          _avatarCache.set(id, dataUrl);
          updated = true;
        }
      });
      if (updated) notify();
    } catch {
      // silently ignore
    }
  },

  /**
   * 解析员工头像：优先使用 IDB 缓存中的 base64，其次使用原始 URL。
   */
  resolveAvatar: (id: string, fallback: string): string => {
    const idbAvatar = _avatarCache.get(id);
    if (idbAvatar) return idbAvatar;
    const ov = state.overrides.get(id);
    if (ov?.avatar && ov.avatar !== IDB_AVATAR_MARKER) return ov.avatar;
    return fallback;
  },

  applyOverridesToTalents: (talents: any[]): any[] => {
    const filtered = talents.filter(t => {
      const ov = state.overrides.get(String(t.id));
      if (!ov) return true;
      if (ov.visibleInUserSide === false) return false;
      return ov.visible !== false;
    });
    const overridden = filtered.map(t => {
      const id = String(t.id);
      const ov = state.overrides.get(id);

      // 解析头像：IDB 缓存 > override URL > 原始 URL
      let resolvedAvatar = t.avatar;
      if (_avatarCache.has(id)) {
        resolvedAvatar = _avatarCache.get(id)!;
      } else if (ov?.avatar && ov.avatar !== IDB_AVATAR_MARKER) {
        resolvedAvatar = ov.avatar;
      }

      if (!ov) return { ...t, avatar: resolvedAvatar };
      return {
        ...t,
        name:        ov.name        ?? t.name,
        avatar:      resolvedAvatar,
        description: ov.description ?? t.description,
        tags:        ov.tags        ?? t.tags,
        salary:      t.salary,
        role:        ov.role        ?? t.role,
        category:    normalizeCategory(ov.category) ?? t.category,
        teamConfig:  ov.teamConfig  ?? t.teamConfig,
      };
    });
    const visibleNew = state.newEmployees.filter(e => {
      const ov = state.overrides.get(e.id);
      return !ov || ov.visible !== false;
    }).map(e => ({
      ...e,
      // 新员工也解析 IDB 头像
      avatar: _avatarCache.get(e.id) ?? (e.avatar !== IDB_AVATAR_MARKER ? e.avatar : ''),
    }));
    return [...overridden, ...visibleNew];
  },

  /** 清除所有覆写（开发调试用） */
  clearAll: () => {
    state.overrides.clear();
    state.newEmployees = [];
    state.syncedIds.clear();
    state.syncTimestamps.clear();
    _avatarCache.clear();
    localStorage.removeItem(STORAGE_KEY);
    notify();
  },

  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
